import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import { usePolling } from "@/lib/usePolling";
import {
  NOTIFY_POLL_MS, allEventIds, newEvents, inAppToast, osNotification,
  canDeliverInAppToast, canDeliverOsNotification, messagesBadgeCount, tabTitle,
} from "@/lib/notifications";

const STAFF_ROLES = ["owner", "provider", "nurse", "front_desk", "system_admin"];

const NotificationContext = createContext(null);

/** The single notification loop: one poll, one feed, every kind of alert.
 *
 *  Mounted ABOVE the routes rather than inside Layout, so navigating between staff
 *  pages does not unmount it. A provider that remounted per route would reset its
 *  "already seen" baseline on every click, and the first poll after each navigation
 *  would be a baseline rather than news — so a message arriving just after somebody
 *  moved from Patients to Schedule would raise no toast at all.
 *
 *  Being above the routes means it is also mounted on the patient portal and on the
 *  login screen, so WHETHER IT RUNS is an explicit gate rather than a consequence of
 *  the tree: `enabled` below. Both halves of that gate are load-bearing —
 *
 *    - `user` falsy (signed out, or signed out BY the idle timer with the tab still
 *      open) stops the poll and stops every alert. An expired session that keeps
 *      announcing patient activity is the unattended-screen failure that automatic
 *      logoff exists to prevent, arriving through a different door.
 *    - a patient session is not staff, so it never polls this feed. The endpoint
 *      refuses a patient anyway (403, require_staff) — this is the second of the two.
 *
 *  Nothing here marks anything read: the feed is a read-only endpoint and no request
 *  made here carries mark_read. Nothing here touches the idle timer either; usePolling
 *  holds that contract for every poll in the app (see lib/polling.js).
 */
export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const navigate = useNavigate();

  const [feed, setFeed] = useState(null);
  const [prefs, setPrefs] = useState({ os_notifications: false });

  // null until the first snapshot lands. newEvents() treats null as "no news yet", which
  // is what stops a wall of toasts for everything unread at sign-in.
  const seen = useRef(null);

  const staff = Boolean(user) && STAFF_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    const { data } = await api.get("/staff/notifications");
    setFeed(data);
    return data;
  }, []);

  useEffect(() => {
    if (!staff) {
      // Signing out drops the feed as well as the loop. Leaving the last snapshot in
      // state would leave a badge and a tab title on screen describing a clinic the
      // browser is no longer signed in to.
      setFeed(null);
      seen.current = null;
      return;
    }
    let stale = false;
    load().catch(() => {});
    api.get("/staff/notification-preferences")
      .then((r) => { if (!stale) setPrefs(r.data); })
      .catch(() => { if (!stale) setPrefs({ os_notifications: false }); });
    return () => { stale = true; };
  }, [staff, load]);

  /* Raise the alerts for whatever is new, then record what was seen. Running inside an
     effect on `feed` rather than inside the fetch keeps the decision out of the request
     path: a failed poll changes nothing, and a re-render cannot replay a toast. */
  useEffect(() => {
    if (!feed || !staff) return;
    const fresh = newEvents(feed, seen.current);
    seen.current = new Set(allEventIds(feed));
    if (!fresh.length) return;

    if (canDeliverInAppToast({ authenticated: staff, locked: false })) {
      fresh.forEach((event) => {
        const t = inAppToast(event);
        if (!t) return;
        toast(t.text, { action: { label: "Open", onClick: () => navigate(t.href) } });
      });
    }

    // `locked: false` on the web portal is a statement, not a stub: the clinic portal
    // has no lock screen — an idle session is logged OUT, which the `authenticated`
    // gate above already covers. The mobile app is the one with a lock, and it does not
    // use this provider.
    if (canDeliverOsNotification({
      authenticated: staff, locked: false,
      optedIn: prefs?.os_notifications,
      permission: typeof Notification === "undefined" ? "unsupported" : Notification.permission,
    })) {
      fresh.forEach((event) => {
        const n = osNotification(event, clinic?.name);
        if (!n) return;
        try {
          new Notification(n.title, { body: n.body });
        } catch {
          // Some browsers throw rather than return a rejected promise when the page is
          // not allowed to construct one. An alert that cannot be shown is not an error
          // worth putting on screen.
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed]);

  // The tab title. Restored on unmount and whenever the count goes to zero, so a tab
  // left open does not keep claiming work that has been done.
  const badge = staff ? messagesBadgeCount(feed) : 0;
  useEffect(() => {
    const base = clinic?.name ? `${clinic.name} · RelationshipOS` : "RelationshipOS";
    document.title = tabTitle(badge, base);
    return () => { document.title = base; };
  }, [badge, clinic?.name]);

  usePolling(load, NOTIFY_POLL_MS, { enabled: staff });

  /** Ask for browser permission. Called from Settings, never on first load: a
   *  permission prompt nobody asked for is the one everybody denies, and a denial is
   *  sticky. */
  const requestOsPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "unsupported";
    if (Notification.permission !== "default") return Notification.permission;
    try {
      return await Notification.requestPermission();
    } catch {
      return Notification.permission;
    }
  }, []);

  const savePrefs = useCallback(async (next) => {
    const { data } = await api.put("/staff/notification-preferences", next);
    setPrefs(data);
    return data;
  }, []);

  return (
    <NotificationContext.Provider
      value={{ feed, badge, prefs, savePrefs, requestOsPermission, refresh: load }}>
      {children}
    </NotificationContext.Provider>
  );
}

/** Safe outside the provider: the login screen renders no badge, and a context hook
 *  that throws there would take the whole page down. */
export const useNotifications = () =>
  useContext(NotificationContext) || {
    feed: null, badge: 0, prefs: { os_notifications: false },
    savePrefs: async () => {}, requestOsPermission: async () => "unsupported",
    refresh: async () => {},
  };
